import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { environment } from '@env/environment';

/**
 * Interceptor funcional para injeção automática de Bearer token
 * e tratamento centralizado de erros HTTP de autenticação/autorização.
 *
 * A lib angular-auth-oidc-client já injeta o token via `secureRoutes`,
 * mas este interceptor adiciona:
 * - Retry automático em 401 (tenta renovar token uma vez)
 * - Redirect para /acesso-negado em 403
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const oidcSecurityService = inject(OidcSecurityService);
  const router = inject(Router);

  const isApiRequest = req.url.startsWith(environment.apiUrl);

  if (!isApiRequest) {
    return next(req);
  }

  return oidcSecurityService.getAccessToken().pipe(
    switchMap(token => {
      const clonedReq = token
        ? req.clone({
            setHeaders: { Authorization: `Bearer ${token}` },
          })
        : req;

      return next(clonedReq).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            return oidcSecurityService.forceRefreshSession().pipe(
              switchMap(result => {
                if (result.isAuthenticated) {
                  return oidcSecurityService.getAccessToken().pipe(
                    switchMap(newToken => {
                      const retryReq = req.clone({
                        setHeaders: { Authorization: `Bearer ${newToken}` },
                      });
                      return next(retryReq);
                    }),
                  );
                }
                oidcSecurityService.authorize();
                return throwError(() => error);
              }),
              catchError(() => {
                oidcSecurityService.authorize();
                return throwError(() => error);
              }),
            );
          }

          if (error.status === 403) {
            router.navigate(['/acesso-negado']);
            return throwError(() => error);
          }

          return throwError(() => error);
        }),
      );
    }),
  );
};
